import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUp, Sparkles, Play } from "lucide-react";
import Header from "@/components/Header";
import { api } from "@/lib/api";

const PROMPTS = [
  "Launch my skincare brand",
  "Become an AI Product Manager",
  "Move into investment banking",
  "Build & launch my startup",
  "Scale my manufacturing career",
];

const STATS = [
  { n: "60+", l: "Competencies" },
  { n: "4", l: "Schools" },
  { n: "1", l: "Path built around you" },
];

export default function Landing() {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const start = async (message) => {
    const msg = (message ?? text).trim();
    if (!msg || loading) return;
    setLoading(true);
    setError("");
    try {
      const session = await api.createSession();
      localStorage.setItem("mentis_sid", session.id);
      // Navigate immediately; Mentis "thinks" in-context on the Build page.
      navigate("/build", { state: { session, pendingMessage: msg } });
      // Safety net: if navigation is ever blocked, never leave the button stuck spinning.
      setTimeout(() => setLoading(false), 4000);
    } catch (e) {
      setError("Mentis couldn't connect just now. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="App min-h-screen relative">
      <div className="absolute inset-0 grid-texture opacity-60 pointer-events-none" />
      <div className="absolute inset-0 spotlight pointer-events-none" />
      <div className="relative z-10">
        <Header variant="landing" />

        <main className="max-w-4xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-24">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="text-center">
            <div className="mono-label text-orange-600 mb-6" data-testid="hero-eyebrow">
              Mentogram Admissions · Powered by Mentis
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900">
              What do you want to<br /><span className="text-orange-grad">become?</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-700 max-w-2xl mx-auto leading-relaxed">
              Tell Mentis where you are today and where you want to go. Mentis will understand your experience,
              map your capabilities and build your learning pathway with you.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-12">
            <div className="surface rounded-3xl p-3 sm:p-4 shadow-2xl">
              <textarea
                ref={inputRef}
                data-testid="hero-prompt-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); start(); } }}
                rows={3}
                placeholder="Tell Mentis what you want to achieve…"
                className="w-full bg-transparent resize-none text-lg text-slate-900 placeholder:text-slate-400 px-3 py-2 scroll-thin"
              />
              <div className="flex items-center justify-between px-2 pb-1">
                <span className="text-xs text-slate-700">Press Enter to begin · Mentis understands natural language</span>
                <button data-testid="mentis-submit-btn" onClick={() => start()} disabled={loading || !text.trim()}
                  className="btn-orange rounded-xl px-4 py-2.5 font-semibold text-sm flex items-center gap-2 disabled:opacity-40">
                  {loading ? "Understanding…" : <>Talk to Mentis <ArrowUp size={16} /></>}
                </button>
              </div>
            </div>

            {error && <div data-testid="hero-error" className="mt-3 text-center text-sm text-red-400">{error}</div>}

            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {PROMPTS.map((p) => (
                <button key={p} data-testid={`prompt-chip-${p.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => { setText(p); inputRef.current?.focus(); }}
                  className="chip rounded-full px-4 py-2 text-[13px] bg-black/[0.03] text-slate-700 border border-black/10 hover:border-orange-500/40 flex items-center gap-1.5">
                  <Sparkles size={12} className="text-orange-600" /> {p}
                </button>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
            className="mt-8 text-center">
            <button data-testid="present-mode-btn" onClick={() => navigate("/build?present=cosmetics")}
              className="text-sm text-slate-700 hover:text-orange-400 transition-colors link-underline inline-flex items-center gap-2">
              <Play size={13} className="text-orange-400" /> Watch Mentis build a pathway live
            </button>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            id="how" className="mt-20 flex flex-wrap justify-center gap-8 sm:gap-16 text-center">
            {STATS.map((s) => (
              <div key={s.l}>
                <div className="font-display text-4xl font-extrabold text-slate-900">{s.n}</div>
                <div className="mono-label text-slate-700 text-[9px] mt-2">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </main>
      </div>
    </div>
  );
}
